import * as React from "react";
import { cva, VariantProps } from "class-variance-authority";

import { cn } from "@src/lib/utils/cn";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-[12px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-background shadow hover:bg-primary-hover",
        secondary:
          "border-transparent bg-secondary text-foreground hover:bg-secondary-hover",
        destructive:
          "border-transparent bg-destructive text-background shadow hover:bg-destructive-hover",
        outline: "text-foreground",
        success:
          "border-transparent text-green-11 bg-green-2 shadow hover:bg-green-3 border-none",
        // Status variants — squared-off pill with a leading colored
        // dot via `::before`. Used for content / workflow lifecycle
        // status indicators.
        published:
          "h-6 px-2 py-0 border-0 leading-[16px] rounded-md gap-1.5 bg-green-1 text-gray-12 before:content-[''] before:size-1.5 before:shrink-0 before:rounded-full before:bg-green-9",
        draft:
          "h-6 px-2 py-0 border-0 leading-[16px] rounded-md gap-1.5 bg-white text-gray-12 before:content-[''] before:size-1.5 before:shrink-0 before:rounded-full before:bg-yellow-9",
        unpublishedChanges:
          "h-6 px-2 py-0 border-0 leading-[16px] whitespace-nowrap rounded-md gap-1.5 bg-white text-gray-12 before:content-[''] before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange-9",
        archived:
          "h-6 px-2 py-0 border-0 leading-[16px] rounded-md gap-1.5 bg-white text-gray-12 before:content-[''] before:size-1.5 before:shrink-0 before:rounded-full before:bg-gray-9",
        // Tone variants — same squared-off shape, tinted by semantic
        // status color. Used for lifecycle states we don't own (e.g.
        // an invoice status coming back from Orb).
        toneSuccess:
          "h-6 px-2 py-0 border-0 leading-[16px] rounded-md bg-status-success-bg text-status-success-text",
        toneWarning:
          "h-6 px-2 py-0 border-0 leading-[16px] rounded-md bg-status-warning-bg text-status-warning-text",
        toneError:
          "h-6 px-2 py-0 border-0 leading-[16px] rounded-md bg-status-error-bg text-status-error-text",
        toneNeutral:
          "h-6 px-2 py-0 border-0 leading-[16px] rounded-md bg-bg-tertiary text-text-secondary",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export type StatusBadgeVariant =
  | "published"
  | "draft"
  | "unpublishedChanges"
  | "archived";

export type ToneBadgeVariant =
  | "toneSuccess"
  | "toneWarning"
  | "toneError"
  | "toneNeutral";

export const STATUS_BADGE_CONFIG: Record<
  string,
  { label: string; variant: StatusBadgeVariant }
> = {
  PUBLISHED: { label: "Published", variant: "published" },
  DRAFT: { label: "Draft", variant: "draft" },
  UNPUBLISHED_CHANGES: {
    label: "Unpublished changes",
    variant: "unpublishedChanges",
  },
  ARCHIVED: { label: "Archived", variant: "archived" },
};

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
